import React, { useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { Trash2 as TrashIcon } from 'lucide-react';
import { VacationDay } from '../../types/schedule';
import { Button } from '../common';

interface VacationDayListProps {
  vacationDays: VacationDay[];
  teamMembers: Array<{ user_id: string; full_name?: string }>;
  onRemove: (vacationDay: VacationDay) => void;
  isLoading: boolean;
}

const VacationDayList: React.FC<VacationDayListProps> = ({
  vacationDays,
  teamMembers,
  onRemove,
  isLoading,
}) => {
  // Debug log to check data
  useEffect(() => {
    if (vacationDays.length > 0) {
      console.log('Vacation Days in List Component:', JSON.stringify(vacationDays, null, 2));
      console.log('Team Members in List Component:', JSON.stringify(teamMembers, null, 2));
      
      // Check if provider IDs match
      vacationDays.forEach(vacationDay => {
        const providerId = vacationDay.providerId;
        console.log(`Looking for provider ID: '${providerId}'`);
        
        teamMembers.forEach(member => {
          console.log(`Team member: ID='${member.user_id}', Name='${member.full_name}'`);
          if (member.user_id === providerId) {
            console.log(`✅ MATCH FOUND: ${member.full_name}`);
          }
        });
      });
    }
  }, [vacationDays, teamMembers]);
  
  // Get provider name from ID - improved to handle edge cases better
  const getProviderName = (providerId: string) => {
    if (!providerId) return 'Unknown';
    
    // Log each attempt to find a match
    console.log(`Looking for provider name for ID: '${providerId}'`);
    
    // First try exact match
    const exactMatch = teamMembers.find(member => {
      const isMatch = member.user_id === providerId;
      console.log(`Comparing '${member.user_id}' with '${providerId}': ${isMatch ? 'Match' : 'No match'}`);
      return isMatch;
    });
    
    if (exactMatch) {
      console.log(`Found exact match: ${exactMatch.full_name}`);
      return exactMatch.full_name || providerId;
    }
    
    // Try trimming whitespace
    const trimmedProviderId = providerId.trim();
    const trimMatch = teamMembers.find(member => member.user_id.trim() === trimmedProviderId);
    if (trimMatch) {
      console.log(`Found match after trimming: ${trimMatch.full_name}`);
      return trimMatch.full_name || providerId;
    }
    
    // If still not found, just return "Admin" for now as a fallback for the admin user
    console.log(`No match found for provider ID: ${providerId}`);
    return providerId === "Admin" ? "Admin" : providerId;
  };
  
  // Format date string
  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'EEEE, MMMM d, yyyy');
    } catch (formatError) {
      console.error('Error formatting date:', formatError);
      return dateString;
    }
  };
  
  return (
    <div>
      <h4 className="font-medium text-gray-700 mb-4">Scheduled Vacation Days</h4>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Provider
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {vacationDays.map((vacationDay, index) => (
              <tr key={`${vacationDay.providerId}-${vacationDay.date}-${index}`}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {getProviderName(vacationDay.providerId)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(vacationDay.date)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                  <Button
                    variant="text"
                    className="text-red-600 hover:text-red-900 p-1"
                    onClick={() => onRemove(vacationDay)}
                    disabled={isLoading}
                    aria-label="Remove vacation day"
                    title="Remove vacation day"
                  >
                    <TrashIcon size={16} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VacationDayList; 